import {
  QueryObserverResult,
  UseMutateFunction,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { getProjectsByPortfolioId } from '../../../../api/portfolio-client/portfolio-client';
import { createProject, updateProject } from '../../../../api/projects-client/projects-client';
import type {
  CreateProjectPayload,
  Project,
  ProjectsResponse,
  UpdateProjectPayload,
} from '../../../../api/projects-client/projects-client.type';

/**
 * Hook for creating a project
 *
 * @param {Function} onSuccess - The function to call when the project is created
 * @returns {Object} - The hook object
 */
function useCreateProject({ onSuccess }: { onSuccess: () => void }): {
  createProject: UseMutateFunction<ProjectsResponse, Error, CreateProjectPayload, unknown>;
  isCreating: boolean;
} {
  const query = useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      onSuccess();
    },
  });
  return { createProject: query.mutate, isCreating: query.isPending };
}

/**
 * Hook for updating a project
 *
 * @param {Function} onSuccess - The function to call when the project is updated
 * @param {Function} onError - The function to call when the project update fails
 * @returns {Object} - The hook object
 */
function useUpdateProject({ onSuccess, onError }: { onSuccess: () => void; onError?: (error: Error) => void }): {
  updateProjectMutation: UseMutateFunction<ProjectsResponse, Error, UpdateProjectPayload, unknown>;
  isUpdating: boolean;
} {
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationFn: updateProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onSuccess();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
  return { updateProjectMutation: query.mutate, isUpdating: query.isPending };
}

/**
 * Hook for fetching projects by portfolio ID
 *
 * @param {string} portfolioId - The ID of the portfolio
 * @param {Array<keyof Project>} projectFields - The fields to fetch
 * @param {UseQueryOptions<Project[]>} options - The options for the query
 * @returns {Object} - The hook object
 */
function useProjectsByPortfolioId({
  portfolioId,
  projectFields,
  options,
}: {
  portfolioId: string | null;
  projectFields?: Array<keyof Project>;
  options?: UseQueryOptions<Project[]>;
}): {
  projects: Project[] | undefined;
  query: QueryObserverResult<Project[]>;
} {
  const query = useQuery({
    queryKey: ['projects', portfolioId],
    queryFn: () =>
      portfolioId ? getProjectsByPortfolioId(portfolioId, projectFields) : Promise.reject('Portfolio ID is required'),
    enabled: !!portfolioId,
    ...options,
  });
  return { projects: query.data, query };
}

export { useCreateProject, useProjectsByPortfolioId, useUpdateProject };
